/**
 * Raw-score explainer — pure helpers for the hover popup.
 *
 * Each group contributes `formula score × group weight`; the contributions are
 * summed (and floored at 0) into the item's raw score. Subformula reference
 * tokens (`__sub_<id>`) are swapped for their readable names, and a blocks-mode
 * group also lists each subformula's actual formula so you can see the math
 * behind every group, not just its result.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { readSubformulaRefId } from '@trafficpulse/shared';

import { fmtScore } from '../../format';
import type { PreviewBreakdownEntry, PreviewRow } from '../../../preview-row.types';

export interface CalcStep {
  label: string;
  formula: string;
  result: string;
  /** Optional sub-lines (e.g. each subformula's own formula). */
  details?: string[];
}

export interface AllocatorBounds {
  minWeight: number;
  maxWeight: number;
}

/**
 * Map of subformula id -> {name, expression, weight}, used to humanize
 * expressions. `weight` is the normalized share (%) within its group, present
 * only when the group is weighted.
 */
export type SubformulaInfo = ReadonlyMap<
  string,
  { name: string; expression: string; weight?: number }
>;

export function buildRawScoreSteps(
  row: PreviewRow,
  subs?: SubformulaInfo,
): CalcStep[] {
  const breakdown = row.breakdown ?? [];
  if (breakdown.length === 0) return [];
  const groupSteps = breakdown.map((entry) => buildGroupStep(entry, subs));
  const sum = breakdown.reduce((total, entry) => total + entry.weightedScore, 0);
  const clamped = Math.max(0, sum);
  const steps = [...groupSteps];
  if (breakdown.length > 1 || sum !== clamped) {
    steps.push({
      label: 'Total raw score',
      formula:
        sum < 0
          ? `${fmtScore(sum)} → max(0, sum)`
          : groupSteps.map((s) => s.result).join(' + '),
      result: fmtScore(clamped),
    });
  }
  return steps;
}

function buildGroupStep(entry: PreviewBreakdownEntry, subs?: SubformulaInfo): CalcStep {
  const weight = resolveGroupWeight(entry);
  const contrib = entry.contributions ?? [];
  if (contrib.length > 0) {
    const parts = contrib.map(
      (c) => `${c.metric} ${fmtScore(c.value)} × ${c.weight}%`,
    );
    return {
      label: entry.groupName,
      formula: `${parts.join(' + ')} → × ${weight}% group`,
      result: fmtScore(entry.weightedScore),
    };
  }
  const raw = entry.expression?.trim() || 'formula';
  return {
    label: entry.groupName,
    formula: `${humanizeExpression(raw, subs)} = ${fmtScore(entry.rawScore)} × ${weight}%`,
    result: fmtScore(entry.weightedScore),
    // Prefer the backend's exact per-sub values (with the weighted math); fall
    // back to name + expression when the payload predates the feature.
    details: subContributionLines(entry, subs) ?? subformulaFormulas(raw, subs),
  };
}

/**
 * Detail lines from the backend's per-sub-formula attribution, showing the real
 * math: "Profit: uEPC = 3.67 × 44% = 1.61" (or "Profit: uEPC = 3.67" unweighted).
 */
function subContributionLines(
  entry: PreviewBreakdownEntry,
  subs?: SubformulaInfo,
): string[] | undefined {
  const sc = entry.subformulaContributions;
  if (!sc || sc.length === 0) return undefined;
  return sc.map((c) => {
    const base = `${c.name}: ${humanizeExpression(c.expression, subs)} = ${fmtScore(c.value)}`;
    return c.weight != null
      ? `${base} × ${c.weight}% = ${fmtScore(c.contribution)}`
      : base;
  });
}

/** Swap `__sub_<id>` reference tokens for their human names where known. */
export function humanizeExpression(expr: string, subs?: SubformulaInfo): string {
  if (!expr.includes('__sub_')) return expr;
  return expr.replace(/__sub_[A-Za-z0-9_]+/g, (token) => {
    const id = readSubformulaRefId(token);
    const name = id ? subs?.get(id)?.name?.trim() : undefined;
    return name || 'subformula';
  });
}

/** "<name> = <formula>" for each subformula referenced in the expression. */
function subformulaFormulas(expr: string, subs?: SubformulaInfo): string[] {
  if (!subs || !expr.includes('__sub_')) return [];
  const ids = new Set<string>();
  for (const match of expr.matchAll(/__sub_([A-Za-z0-9_]+)/g)) ids.add(match[1]);
  const lines: string[] = [];
  for (const id of ids) {
    const info = subs.get(id);
    if (info?.expression) {
      const pct = info.weight != null ? ` (${info.weight}%)` : '';
      lines.push(`${info.name}${pct} = ${humanizeExpression(info.expression, subs)}`);
    }
  }
  return lines;
}

function resolveGroupWeight(entry: PreviewBreakdownEntry): number {
  if (entry.groupWeight != null && Number.isFinite(entry.groupWeight)) {
    return Math.round(entry.groupWeight * 100) / 100;
  }
  if (entry.rawScore === 0) return entry.weightedScore === 0 ? 0 : 100;
  return Math.round((entry.weightedScore / entry.rawScore) * 10000) / 100;
}
